'use client';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useRouter } from '@/navigation';
import { LoadStatuses, useLoadScript } from '@/shared/hooks/dom/useLoadScript';
import { Loader } from '@/shared/ui/loader';
import btnIcons from '@/shared/assets/sprites/btns.sprite.svg';
import type { IGeo } from '@/entities/medcenter/model/types';
interface Props {
  geo: IGeo;
}
export const FullScreenMap: React.FC<Props> = ({
  geo
}) => {
  const mapUrl = process.env.NEXT_PUBLIC_MAP_URL;
  const router = useRouter();
  const {
    status,
    start: startLoad
  } = useLoadScript(mapUrl, {
    async: true
  });
  const [isLoading, setIsLoading] = useState(true);
  const mapContainer = useRef<HTMLDivElement | null>(null);
  const mapInstance = useRef<unknown>(null);
  const markerInstance = useRef<unknown>(null);
  const goBack = () => {
    setIsLoading(true);
    router.back();
  };
  const initializeMap = useCallback(() => {
    if (!window.DG || !mapContainer.current || !geo) return;
    try {
      if (!mapInstance.current) {
        mapInstance.current = new window.DG.Map(mapContainer.current, {
          center: [geo.latitude, geo.longitude],
          zoom: 15,
          zoomControl: false,
          fullscreenControl: false
        });
      } else {
        // @ts-ignore
        mapInstance.current.setView([geo.latitude, geo.longitude], 13);
      }
      if (markerInstance.current) {
        // @ts-ignore
        mapInstance.current.removeLayer(markerInstance.current);
      }
      markerInstance.current = window.DG.marker([geo.latitude, geo.longitude]).addTo(mapInstance.current);
    } catch (error) {
      console.error('Map initialization error:', error);
    } finally {
      setIsLoading(false);
    }
  }, [geo]);
  const cleanupMap = useCallback(() => {
    if (mapInstance.current) {
      // @ts-ignore
      mapInstance.current.remove();
      mapInstance.current = null;
      markerInstance.current = null;
    }
  }, []);
  useEffect(() => {
    if (window.DG || status === LoadStatuses.LOADED) {
      window.DG.then(initializeMap);
    } else {
      startLoad();
    }
    return () => {
      cleanupMap();
    };
  }, [status]);
  return <div className="fixed inset-0 z-[100]" data-sentry-component="FullScreenMap" data-sentry-source-file="FullScreenMap.tsx">
      <div ref={mapContainer} className="size-full"></div>

      <button onClick={goBack} className="fixed right-4 top-4 z-[99] flex size-8 items-center justify-center rounded-full bg-bgPrimary md:right-10 md:top-10">
        <svg className="size-8 rotate-180 stroke-textPrimary" data-sentry-element="svg" data-sentry-source-file="FullScreenMap.tsx">
          <use xlinkHref={`${btnIcons}#close`} data-sentry-element="use" data-sentry-source-file="FullScreenMap.tsx"></use>
        </svg>
      </button>

      {isLoading && <>
          <div className="fixed inset-0 z-[98] backdrop-blur-sm"></div>
          <div className="fixed left-1/2 top-1/2 z-[99] -translate-x-1/2 -translate-y-1/2">
            <Loader size="4xl" />
          </div>
        </>}
    </div>;
};